import { Link, useSearchParams } from 'react-router-dom';
import type { RevenueByTenantRow } from '@nadoutong/contracts';
import { usePricing } from '../../api/pricing.ts';
import { useRevenueByTenant } from '../../api/revenue.ts';
import {
  formatCnyCents,
  formatUsdMicrosOrCentsAsCnyDisplay,
  usdMicrosFromCentsFallback,
  usdMicrosToCnyCents,
} from '../../components/formatters.ts';
import { CustomerCell } from '../../components/identity/CustomerCell.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Panel, Skeleton, TBody, Table, Td, Th, THead, Tr } from '../../ui/index.ts';
import { parseTenantSort } from './hubState.ts';
import type { MetricsTabContext, TenantSort } from './hubState.ts';

/**
 * 租户 tab (epic #1643): the top-tenant table under two URL-backed
 * lenses (`?tenantSort=spend|profit`) — the server sorts BEFORE the
 * limit truncation, so each lens is the true top-N. Rows drill into
 * the account detail and its request log (same targets as the usage
 * overview's tenant table, #1008).
 */

const TENANT_LIMIT = 50;
const COUNT_FORMATTER = new Intl.NumberFormat('en-US');

export function TenantsTab({ context }: { readonly context: MetricsTabContext }): JSX.Element {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const sort = parseTenantSort(params.get('tenantSort'));
  const { brand, range } = context;
  const query = useRevenueByTenant({
    limit: TENANT_LIMIT,
    brand,
    sort,
    from: range.from,
    to: range.to,
  });
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;

  function setSort(next: TenantSort): void {
    const clone = new URLSearchParams(params);
    if (next === 'spend') clone.delete('tenantSort');
    else clone.set('tenantSort', next);
    setParams(clone, { replace: true });
  }

  return (
    <Panel
      caption={t('metrics.tenants.title')}
      actions={
        <div role="tablist" aria-label={t('metrics.tenants.sort.aria')} className="inline-flex gap-1">
          {(['spend', 'profit'] as const).map((option) => {
            const active = option === sort;
            return (
              <Button
                key={option}
                role="tab"
                aria-selected={active}
                variant={active ? 'secondary' : 'ghost'}
                size="sm"
                className={active ? 'bg-active text-paper' : undefined}
                data-testid={`metrics-tenant-sort-${option}`}
                onClick={() => setSort(option)}
              >
                {option === 'spend' ? t('metrics.tenants.sort.spend') : t('metrics.tenants.sort.profit')}
              </Button>
            );
          })}
        </div>
      }
      testId="metrics-tenants-tab"
    >
      {query.isPending ? (
        <Skeleton className="m-4 h-40" testId="metrics-tenants-loading" />
      ) : query.isError ? (
        <p className="m-0 p-4 text-body text-bad-bright" data-testid="metrics-tenants-error">
          {t('metrics.tenants.error')}
        </p>
      ) : query.data === undefined || query.data.tenants.length === 0 ? (
        <p className="m-0 p-4 text-body text-paper-low" data-testid="metrics-tenants-empty">
          {t('metrics.tenants.empty')}
        </p>
      ) : (
        <Table className="border-none">
          <THead>
            <Tr interactive={false}>
              <Th>{t('metrics.tenants.col.account')}</Th>
              <Th num>{t('metrics.tenants.col.requests')}</Th>
              <Th num>{t('metrics.tenants.col.charged')}</Th>
              <Th num>{t('metrics.tenants.col.cost')}</Th>
              <Th num>{t('metrics.tenants.col.profit')}</Th>
              <Th num>{t('metrics.tenants.col.margin')}</Th>
            </Tr>
          </THead>
          <TBody>
            {query.data.tenants.map((row) => (
              <Tr key={row.account_id}>
                <Td>
                  <CustomerCell
                    accountId={row.account_id}
                    testId={`metrics-tenant-customer-${row.account_id}`}
                    secondary={
                      <div>
                        <Link
                          to={`/customers/accounts/${encodeURIComponent(row.account_id)}`}
                          className="font-medium text-paper transition-colors duration-120 hover:text-signal"
                          data-testid={`metrics-tenant-account-${row.account_id}`}
                        >
                          {row.account_email.length > 0 ? row.account_email : row.account_id}
                        </Link>
                        <div className="font-mono text-micro text-paper-low">{row.account_id}</div>
                        <Link
                          to={`/customers/requests?accountId=${encodeURIComponent(row.account_id)}`}
                          className="mt-0.5 inline-block font-mono text-micro text-signal transition-colors duration-120 hover:underline"
                          data-testid={`metrics-tenant-requests-${row.account_id}`}
                        >
                          {t('metrics.tenants.view_requests')}
                        </Link>
                      </div>
                    }
                  />
                </Td>
                <Td num>{COUNT_FORMATTER.format(row.usage_count)}</Td>
                <Td num className="text-ok">
                  {formatUsdMicrosOrCentsAsCnyDisplay(
                    row.usage_usd_charged_micros,
                    row.usage_usd_charged_cents,
                    cnyPerUsd, false)}
                </Td>
                <Td num className="text-bad">{formatCnyCents(row.usage_cny_upstream_cost_cents, false)}</Td>
                <Td num className="whitespace-nowrap">{formatCnyCents(row.l2_profit_cny_cents, true)}</Td>
                <Td num>{marginLabel(row, cnyPerUsd)}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Panel>
  );
}

/**
 * The by-tenant contract carries no server-computed margin, so this is
 * a display-only ratio: L2 profit over display-rate-converted charged
 * revenue. Unknown rate or zero revenue → '—' (never NaN).
 */
function marginLabel(row: RevenueByTenantRow, cnyPerUsd: number | null): string {
  const revenueCnyCents = usdMicrosToCnyCents(
    usdMicrosFromCentsFallback(row.usage_usd_charged_micros, row.usage_usd_charged_cents),
    cnyPerUsd,
  );
  if (revenueCnyCents === null || revenueCnyCents === 0) return '—';
  return `${((row.l2_profit_cny_cents / revenueCnyCents) * 100).toFixed(1)}%`;
}
